'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { Check, Copy, Download, Loader2, RefreshCw, Search, X } from 'lucide-react';

import {
  EMPTY_PROXY_QUERY,
  PROXY_PAGE_SIZE,
  PROXY_SORT_FIELDS,
  hasProxyFilters,
  proxyExportUrl,
  proxyQueryToParams,
  proxyQueryToSearch,
  type ProxyFacet,
  type ProxyListResponse,
  type ProxyQuery,
  type ProxySortField,
} from '@/lib/proxy-list';

/**
 * Таблица публичных прокси.
 *
 * Фильтрует не она, а API: список пересобирается обходом каждые
 * несколько минут, и отобранная в браузере копия устаревала бы прямо
 * на глазах. Кроме того, гостю адреса приходят скрытыми, и полного
 * списка на клиенте нет в принципе — фильтровать было бы нечего.
 *
 * Состояние держится в строке запроса. Ссылка «socks5 из Германии
 * быстрее 500 мс» осмысленна сама по себе: её отправляют и кладут в
 * закладки, а страница, забывающая фильтры при перезагрузке, выглядит
 * сломанной.
 */

/** Через сколько после последнего нажатия уходит запрос поиска. */
const SEARCH_DEBOUNCE_MS = 350;

/** Как часто список обновляется сам. Обход на сервере идёт примерно с
 *  тем же шагом, чаще спрашивать нечего. */
const REFRESH_MS = 60_000;

interface Props {
  /** Выдача, отрисованная на сервере: таблица видна сразу, без
   *  пустого экрана на время первого запроса. */
  initial: ProxyListResponse;
  /** Фильтры, с которыми эта выдача получена. */
  initialQuery: ProxyQuery;
}

export function ProxyTable({ initial, initialQuery }: Props) {
  const t = useTranslations('dh_proxy');

  const [query, setQuery] = useState<ProxyQuery>(initialQuery);
  const [data, setData] = useState<ProxyListResponse>(initial);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Поле поиска живёт отдельно от фильтров: запрос уходит не на каждую
  // букву, иначе набор «185.» стоил бы четырёх обращений подряд.
  const [searchText, setSearchText] = useState(initialQuery.search);

  // Первый проход обычно пропускается: данные уже пришли с сервера, и
  // повторный запрос тем же адресом нарисовал бы то же самое.
  //
  // Исключение — пустая выдача при незаданных фильтрах. Так выглядит
  // отказ внешнего API на сервере: страница отдаётся целиком, но с
  // пустой таблицей (см. `fetchFirstPage`). Один сетевой отказ в момент
  // отрисовки оставлял бы человека перед словами «список пуст» до
  // следующего самообновления, то есть на минуту, хотя список живой.
  const firstRun = useRef(initial.items.length > 0 || hasProxyFilters(initialQuery));

  const load = useCallback(async (next: ProxyQuery, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = proxyQueryToParams(next);
      const response = await fetch(`/api/proxy-list?${params.toString()}`, { signal });
      if (!response.ok) throw new Error(`http ${String(response.status)}`);
      setData((await response.json()) as ProxyListResponse);
      setFailed(false);
    } catch (error) {
      // Отменённый запрос — не отказ: так выглядит следующая набранная
      // буква, и показывать из-за неё ошибку означало бы мигать
      // красным при обычном наборе.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Фильтры изменились: обновляем адрес и перезапрашиваем.
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    const controller = new AbortController();
    // Адрес меняется без перезагрузки: страница уже показывает нужное, а
    // повторный проход по серверу стоил бы мигания таблицы.
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${proxyQueryToSearch(query)}`,
    );
    void load(query, controller.signal);

    return () => {
      controller.abort();
    };
  }, [query, load]);

  // Строка поиска: ждём паузу в наборе.
  useEffect(() => {
    if (searchText === query.search) return;

    const timer = setTimeout(() => {
      setQuery((q) => ({ ...q, search: searchText, offset: 0 }));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText, query.search]);

  // Список живёт своей жизнью: пока человек читает таблицу, часть
  // прокси умирает. Тихое обновление честнее неподвижной картинки.
  useEffect(() => {
    const timer = setInterval(() => {
      void load(query);
    }, REFRESH_MS);

    return () => {
      clearInterval(timer);
    };
  }, [query, load]);

  const toggle = useCallback((field: 'protocol' | 'countries' | 'anonymity', value: string) => {
    setQuery((q) => {
      const current = q[field];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...q, [field]: next, offset: 0 };
    });
  }, []);

  const sortBy = useCallback((field: ProxySortField) => {
    setQuery((q) =>
      // Повторное нажатие по той же колонке переворачивает порядок:
      // так ведут себя таблицы везде, и объяснять это не приходится.
      q.sort === field
        ? { ...q, desc: !q.desc, offset: 0 }
        : { ...q, sort: field, desc: false, offset: 0 },
    );
  }, []);

  const reset = useCallback(() => {
    setSearchText('');
    setQuery(EMPTY_PROXY_QUERY);
  }, []);

  // Шаг листания берётся из ответа, а не из запроса. Гостю API отдаёт
  // двадцать записей независимо от запрошенного размера страницы, и
  // листание по своему значению оставляло бы дыры: со страницы с
  // первыми двадцатью строками кнопка «вперёд» прыгала бы на пятьдесят
  // первую, а тридцать записей между ними не открывались бы вовсе.
  const step = data.limit > 0 ? data.limit : PROXY_PAGE_SIZE;
  const page = Math.floor(query.offset / step) + 1;
  const pages = Math.max(1, Math.ceil(data.total / step));
  const filtered = hasProxyFilters(query);

  return (
    <div className="max-w-[1280px] mx-auto px-6 pb-20">
      <Summary data={data} loading={loading} />

      {data.masked && <MaskedNotice />}

      {/* `min-w-0` у колонки с таблицей обязателен.

          Элемент грида по умолчанию имеет `min-width: auto`, то есть не
          сжимается уже своего содержимого. Таблица прокси шире телефона
          (измерено: 717px против 360px), и без этого правила колонка
          распирала страницу: уезжала вся вёрстка целиком, вместе с
          шапкой и подвалом, а не прокручивалась одна таблица, для чего
          и заведён `overflow-x-auto` ниже.

          То же ограничение стоит у боковой колонки: длинное название
          страны в фильтрах растягивало бы её так же. */}
      <div className="mt-8 grid lg:grid-cols-[260px_minmax(0,1fr)] gap-8 items-start">
        <aside className="min-w-0 space-y-6 lg:sticky lg:top-24">
          <div>
            <label htmlFor="proxy-search" className="block text-sm font-medium mb-2">
              {t('search_label')}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
              <input
                id="proxy-search"
                type="search"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                placeholder={t('search_placeholder')}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm outline-none focus:border-[var(--color-text-muted)]"
              />
            </div>
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t('search_hint')}</p>
          </div>

          <FacetGroup
            title={t('filter_protocol')}
            values={data.facets.protocols}
            selected={query.protocol}
            onToggle={(v) => {
              toggle('protocol', v);
            }}
          />

          <FacetGroup
            title={t('filter_anonymity')}
            values={data.facets.anonymity}
            selected={query.anonymity}
            onToggle={(v) => {
              toggle('anonymity', v);
            }}
            label={(v) => (v === 'elite' || v === 'transparent' ? t(`anonymity_${v}`) : v)}
          />

          <FacetGroup
            title={t('filter_country')}
            // Стран в выдаче бывает несколько десятков, и полный список
            // занял бы экран целиком. Выбранные показываются всегда:
            // фильтр, пропавший из списка после нажатия, выглядел бы
            // сбросом.
            values={data.facets.countries}
            selected={query.countries}
            onToggle={(v) => {
              toggle('countries', v);
            }}
            limit={12}
            moreLabel={t('show_all')}
            label={(v) => `${countryFlag(v)} ${v}`}
          />

          <div>
            <span className="block text-sm font-medium mb-2">{t('filter_latency')}</span>
            <div className="flex flex-wrap gap-2">
              {[500, 1000, 3000].map((ms) => (
                <button
                  key={ms}
                  type="button"
                  onClick={() => {
                    setQuery((q) => ({
                      ...q,
                      maxLatency: q.maxLatency === ms ? 0 : ms,
                      offset: 0,
                    }));
                  }}
                  aria-pressed={query.maxLatency === ms}
                  className={chipClass(query.maxLatency === ms)}
                >
                  {t('latency_under', { ms })}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium mb-2">{t('filter_https')}</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setQuery((q) => ({
                    ...q,
                    https: q.https === true ? undefined : true,
                    offset: 0,
                  }));
                }}
                aria-pressed={query.https === true}
                className={chipClass(query.https === true)}
              >
                {t('https_only')}
              </button>
            </div>
          </div>

          {filtered && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
            >
              <X className="w-4 h-4" />
              {t('reset')}
            </button>
          )}
        </aside>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('found', { total: data.total })}
            </p>
            {!data.masked && <ExportButtons query={query} label={t('export')} />}
          </div>

          {failed && <p className="mb-4 text-sm text-[var(--color-accent-400)]">{t('error')}</p>}

          <div className="overflow-x-auto border border-[var(--color-border)] rounded-2xl bg-[var(--color-bg-card)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left">
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('col_address')}
                  </th>
                  {PROXY_SORT_FIELDS.filter((f) => f !== 'latency').map((field) => (
                    <SortableHeader
                      key={field}
                      label={t(`col_${field}`)}
                      active={query.sort === field}
                      desc={query.desc}
                      onClick={() => {
                        sortBy(field);
                      }}
                    />
                  ))}
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('col_https')}
                  </th>
                  <SortableHeader
                    label={t('col_latency')}
                    active={query.sort === 'latency'}
                    desc={query.desc}
                    onClick={() => {
                      sortBy('latency');
                    }}
                  />
                </tr>
              </thead>
              <tbody>
                {/* Ключ с номером строки, а не один адрес: у гостя адреса
                    замаскированы, и два разных прокси из одной подсети
                    превращаются в одинаковый `socks5://144.***.***.***:***`.
                    React на это отвечает предупреждением о повторяющихся
                    ключах и вправе выбросить одну из строк. */}
                {data.items.map((item, index) => (
                  <tr
                    key={`${item.proxy}-${String(index)}`}
                    className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-bg-elevated)] transition-colors"
                  >
                    <td className="px-4 py-2.5 font-mono whitespace-nowrap">
                      <AddressCell value={item.proxy} masked={data.masked} copyLabel={t('copy')} />
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{item.protocol}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {countryFlag(item.geolocation.country)} {item.geolocation.country}
                      {item.geolocation.city !== '' &&
                        item.geolocation.city.toLowerCase() !== 'unknown' && (
                          <span className="text-[var(--color-text-muted)]">
                            {' '}
                            · {item.geolocation.city}
                          </span>
                        )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {item.anonymity === 'elite' || item.anonymity === 'transparent'
                        ? t(`anonymity_${item.anonymity}`)
                        : item.anonymity}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {item.https ? <Check className="w-4 h-4" aria-label={t('yes')} /> : '—'}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Latency ms={item.latency} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data.items.length === 0 && (
              <p className="px-4 py-10 text-center text-[var(--color-text-muted)]">
                {/* «Список ещё собирается» и «ничего не нашлось» лечатся
                    по-разному: в первом случае надо подождать, во втором
                    ослабить фильтры. */}
                {data.refreshing
                  ? t('empty_refreshing')
                  : filtered
                    ? t('empty_filtered')
                    : t('empty')}
              </p>
            )}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between gap-4 mt-4">
              <button
                type="button"
                disabled={query.offset === 0}
                onClick={() => {
                  setQuery((q) => ({ ...q, offset: Math.max(0, q.offset - step) }));
                }}
                className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm disabled:opacity-40 hover:bg-[var(--color-bg-elevated)] transition-colors"
              >
                {t('prev')}
              </button>
              <span className="text-sm text-[var(--color-text-muted)]">
                {t('page_of', { page, pages })}
              </span>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => {
                  setQuery((q) => ({ ...q, offset: q.offset + step }));
                }}
                className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm disabled:opacity-40 hover:bg-[var(--color-bg-elevated)] transition-colors"
              >
                {t('next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Сводка над таблицей: сколько живых, когда проверяли. */
function Summary({ data, loading }: { data: ProxyListResponse; loading: boolean }) {
  const t = useTranslations('dh_proxy');

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[var(--color-text-muted)]">
      <span className="inline-flex items-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        {/* Время проверки — главное число этой страницы: сп��сок прокси
            без даты обхода ничем не отличается от списка мёртвых. */}
        {data.updated_at ? t('updated_at', { time: formatTime(data.updated_at) }) : t('updating')}
      </span>
      <span>{t('checked', { count: data.checked })}</span>
      {data.refreshing && <span>{t('refreshing')}</span>}
    </div>
  );
}

/**
 * Предложение войти: у гостя адреса скрыты.
 *
 * Ссылка, а не кнопка с переходом в обработчике: вход открывается в
 * новой вкладке средним щелчком, показывает адрес в строке состояния и
 * доступен с клавиатуры без нашего участия. Навигация берётся
 * локализованная — `useRouter` из `next/navigation` не знает о локали,
 * и русский посетитель уезжал бы на английскую страницу входа.
 */
function MaskedNotice() {
  const t = useTranslations('dh_proxy');

  return (
    <div className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] px-5 py-4 flex flex-wrap items-center justify-between gap-4">
      <p className="text-sm text-[var(--color-text-muted)] max-w-xl">{t('masked_notice')}</p>
      <Link
        href="/login"
        className="dh-cta px-5 py-2 rounded-full text-sm font-medium transition-colors"
      >
        {t('masked_cta')}
      </Link>
    </div>
  );
}

/** Выгрузка списка с текущими фильтрами. */
function ExportButtons({ query, label }: { query: ProxyQuery; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className="text-[var(--color-text-muted)]">{label}</span>
      {(['txt', 'csv'] as const).map((format) => (
        <a
          key={format}
          href={proxyExportUrl(query, format)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          {format.toUpperCase()}
        </a>
      ))}
    </span>
  );
}

/** Заголовок колонки, по которой можно упорядочить. */
function SortableHeader({
  label,
  active,
  desc,
  onClick,
}: {
  label: string;
  active: boolean;
  desc: boolean;
  onClick: () => void;
}) {
  return (
    <th
      scope="col"
      // Направление сортировки объявляется разметкой, а не только
      // стрелкой: иначе для экранного диктора колонки неразличимы.
      aria-sort={active ? (desc ? 'descending' : 'ascending') : 'none'}
      className="px-4 py-3 font-medium"
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 hover:text-[var(--color-accent-400)] transition-colors"
      >
        {label}
        <span aria-hidden className={active ? '' : 'opacity-30'}>
          {active && desc ? '↓' : '↑'}
        </span>
      </button>
    </th>
  );
}

/** Группа значений фильтра со счётчиками. */
function FacetGroup({
  title,
  values,
  selected,
  onToggle,
  limit,
  moreLabel,
  label = (v) => v,
}: {
  title: string;
  values: ProxyFacet[];
  selected: string[];
  onToggle: (value: string) => void;
  limit?: number;
  moreLabel?: string;
  label?: (value: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);

  const shown = useMemo(() => {
    if (limit === undefined || expanded) return values;
    const head = values.slice(0, limit);
    // Выбранное значение может оказаться за пределом списка: без этого
    // нажатие на страну из «показать все» тут же прятало бы фильтр.
    const missing = values.filter(
      (v) => selected.includes(v.value) && !head.some((h) => h.value === v.value),
    );
    return [...head, ...missing];
  }, [values, limit, expanded, selected]);

  if (values.length === 0) return null;

  return (
    <div>
      <span className="block text-sm font-medium mb-2">{title}</span>
      <div className="flex flex-wrap gap-2">
        {shown.map((facet) => {
          const active = selected.includes(facet.value);
          return (
            <button
              key={facet.value}
              type="button"
              onClick={() => {
                onToggle(facet.value);
              }}
              aria-pressed={active}
              className={chipClass(active)}
            >
              {label(facet.value)}
              <span className="ml-1.5 opacity-60">{facet.count}</span>
            </button>
          );
        })}
      </div>
      {limit !== undefined && !expanded && values.length > shown.length && moreLabel && (
        <button
          type="button"
          onClick={() => {
            setExpanded(true);
          }}
          className="mt-2 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
        >
          {moreLabel} ({values.length})
        </button>
      )}
    </div>
  );
}

/** Адрес с кнопкой копирования. У гостя копировать нечего. */
function AddressCell({
  value,
  masked,
  copyLabel,
}: {
  value: string;
  masked: boolean;
  copyLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  if (masked) return <span className="text-[var(--color-text-muted)]">{value}</span>;

  return (
    <span className="inline-flex items-center gap-2">
      {value}
      <button
        type="button"
        title={copyLabel}
        aria-label={copyLabel}
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 1500);
          });
        }}
        className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
}

/**
 * Время отклика с цветовой подсказкой.
 *
 * Голое число мало что говорит: чтобы выбрать прокси, человек всё равно
 * сравнивает его с соседними. Три ступени отвечают на вопрос сразу.
 */
export function Latency({ ms }: { ms: number }) {
  const tone =
    ms < 500 ? 'text-emerald-500' : ms < 1500 ? 'text-amber-500' : 'text-[var(--color-text-muted)]';

  return <span className={tone}>{ms} ms</span>;
}

function chipClass(active: boolean): string {
  return [
    'px-3 py-1.5 rounded-full border text-xs transition-colors',
    active
      ? 'border-[var(--color-text)] bg-[var(--color-bg-elevated)] text-[var(--color-text)]'
      : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-muted)]',
  ].join(' ');
}

/**
 * Флаг по коду страны.
 *
 * Коды преобразуются в региональные символы Unicode: держать сотню
 * картинок ради двухбуквенного кода незачем. `ZZ` — не страна, а метка
 * «неизвестно» в источнике, и флага у неё нет.
 */
export function countryFlag(code: string): string {
  if (code.length !== 2 || code.toUpperCase() === 'ZZ') return '🏳';
  // Буквы берутся по индексам, а не разбором строки: код всегда ровно
  // две латинские буквы, а любой разбор на символы линтер справедливо
  // подозревает в порче эмодзи и нелатинских букв.
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    0x1f1e6 + (upper.charCodeAt(0) - 65),
    0x1f1e6 + (upper.charCodeAt(1) - 65),
  );
}

/** Время обхода в часовом поясе читателя. */
function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
